import { Moment } from 'moment';
import React, { useEffect, useMemo, useState } from 'react';

import { Selectable, SelectableInfo } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  Employee,
  EmployeeGroup,
  IncidentStatus,
  IncidentType,
  Organization,
} from '@vestahealthcare/common/models';

import {
  DateTimePicker,
  FilterItem,
  Panel,
  Select,
  TextInput,
} from 'styleguide-v2';
import { FilterGroup } from 'styleguide-v2/src/components/Panel';
import { FilterEmployeeOptions } from 'styleguide-v2/src/components/SelectAssignee';

import { GetIncidentParams } from 'dash/src/services/IncidentServices';
import {
  getBaseEnumFromFilter,
  getFilterCount,
  getFilterItem,
  getFilterValue,
  getSafeValues,
} from 'dash/src/utils/filterUtils';

import { INCIDENTS_PAGE_TAB_OPEN, IncidentPageTabType } from './types';

type KeyGetIncidentParams = keyof GetIncidentParams;

const INCIDENT_FILTERS: KeyGetIncidentParams[] = [
  'assigneeId',
  'from',
  'id',
  'status',
  'to',
  'type',
];
const MEMBER_FILTERS: KeyGetIncidentParams[] = [
  'brandId',
  'communityResourceId',
  'excludedReferralSourceId',
  'memberId',
  'memberName',
  'referralSourceId',
];

type Props = {
  data: {
    assignees: SelectableInfo<Employee>[];
    assigneeGroups: SelectableInfo<EmployeeGroup>[];
    brands: Selectable[];
    communityResources: Selectable[];
    incidentTypes: IncidentType[];
    incidentStatuses: IncidentStatus[];
    nps: Selectable[];
    referralSources: SelectableInfo<Organization>[];
  };
  filters?: {
    [x in KeyGetIncidentParams]?: FilterItem;
  };
  loadingFilters?: boolean;
  open: boolean;
  onChange: (
    filters: {
      [x in KeyGetIncidentParams]?: FilterItem;
    },
  ) => void;
  onClose: () => void;
  tab: IncidentPageTabType;
};

export const IncidentsFilterModal = ({
  data,
  filters,
  loadingFilters,
  onChange,
  onClose,
  open,
  tab,
}: Props) => {
  const [pendingFilters, setPendingFilters] = useState<
    {
      [x in KeyGetIncidentParams]?: FilterItem;
    }
  >(filters || {});

  useEffect(() => {
    setPendingFilters(filters || {});
  }, [filters]);

  const INCIDENT_OPEN_STATUSES = useMemo(
    () =>
      data.incidentStatuses
        ?.filter(({ isClosed }) => !isClosed)
        ?.map(({ id }) => id),
    [data.incidentStatuses],
  );

  return (
    <Panel.FilterModal
      open={open}
      onClear={() => {
        onChange({});
      }}
      onClose={onClose}
      onSubmit={() => {
        if (pendingFilters) {
          onChange(pendingFilters);
        }
      }}
    >
      <FilterGroup
        title="Member Details"
        count={getFilterCount(pendingFilters, MEMBER_FILTERS)}
      >
        <TextInput
          data-cy="incidents-filters-name"
          label={translate('incidents.filters.memberName')}
          onChange={(name?: string) =>
            setPendingFilters({
              ...pendingFilters,
              memberName: getFilterItem(
                translate('incidents.filters.memberName'),
                name,
              ),
            })
          }
          size="small"
          value={getFilterValue(pendingFilters.memberName?.value) as string}
        />
        <Select
          allowAddNewValue
          data-cy="incidents-filter-id"
          items={[]}
          inputType="number"
          multiple
          noOptionsText={translate('members.typeToSearch')}
          onChange={(values: Selectable[]) =>
            setPendingFilters({
              ...pendingFilters,
              memberId: getFilterItem(
                translate('incidents.filters.memberId'),
                values.map(({ label }) => ({
                  value: label,
                  label,
                })),
              ),
            })
          }
          label={translate('incidents.filters.memberId')}
          size="small"
          value={pendingFilters.memberId?.value}
        />
        <Select
          data-cy="incidents-filter-np-owner"
          filterOptions={FilterEmployeeOptions}
          items={data.nps}
          label={translate('incidents.filters.npOwner')}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              npOwnerId: getFilterItem(
                translate('incidents.filters.npOwner'),
                values,
              ),
            });
          }}
          value={pendingFilters.npOwnerId?.value}
          size="small"
        />
        <Select
          data-cy="incidents-filter-referral-sources"
          getItemDisabled={(child) =>
            !!(getSafeValues(
              data.referralSources,
              getFilterValue(
                pendingFilters?.referralSourceId?.value,
              ) as number[],
            ) as SelectableInfo<Organization>[])?.reduce(
              (acc, item) => acc || !!item.info?.hasDescendant(child.value),
              false,
            )
          }
          items={data.referralSources}
          label={translate('incidents.filters.referralSources')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              referralSourceId: getFilterItem(
                translate('incidents.filters.referralSources'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.referralSourceId?.value || []}
        />
        <Select
          data-cy="incidents-filter-excluded-referral-sources"
          getItemDisabled={(child) =>
            !!(getSafeValues(
              data.referralSources,
              getFilterValue(
                pendingFilters?.excludedReferralSourceId?.value,
              ) as number[],
            ) as SelectableInfo<Organization>[])?.reduce(
              (acc, item) => acc || !!item.info?.hasDescendant(child.value),
              false,
            )
          }
          items={data.referralSources}
          label={translate('incidents.filters.excludedReferralSources')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              excludedReferralSourceId: getFilterItem(
                translate('incidents.filters.excludedReferralSources'),
                values,
              ),
            });
          }}
          size="small"
          value={pendingFilters.excludedReferralSourceId?.value || []}
        />

        <Select
          data-cy="incidents-filter-community-resource"
          items={data.communityResources}
          label={translate('incidents.filters.homeHealth')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              communityResourceId: getFilterItem(
                translate('incidents.filters.homeHealth'),
                values,
              ),
            });
          }}
          value={pendingFilters.communityResourceId?.value}
          size="small"
        />

        <Select
          data-cy="incidents-filter-brand"
          items={data.brands}
          label={translate('incidents.filters.brand')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(values: Selectable[]) => {
            setPendingFilters({
              ...pendingFilters,
              brandId: getFilterItem(
                translate('incidents.filters.brand'),
                values,
              ),
            });
          }}
          value={pendingFilters.brandId?.value}
          size="small"
        />
      </FilterGroup>

      <FilterGroup
        title="Incident Details"
        count={getFilterCount(pendingFilters, INCIDENT_FILTERS)}
      >
        <TextInput
          data-cy="incidents-filters-id"
          label={translate('incidents.filters.incidentId')}
          onChange={(name?: string) =>
            setPendingFilters({
              ...pendingFilters,
              id: getFilterItem(
                translate('incidents.filters.incidentId'),
                name,
              ),
            })
          }
          size="small"
          value={getFilterValue(pendingFilters.id?.value) as string}
          type="number"
        />

        <Select
          className="grid-span-3"
          data-cy="incidents-filter-type"
          getItemLabel={({ description }: IncidentType) => description}
          items={data.incidentTypes}
          label={translate('incidents.filters.type')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(type: IncidentType[]) =>
            setPendingFilters({
              ...pendingFilters,
              type: getFilterItem(translate('incidents.filters.type'), type),
            })
          }
          value={getBaseEnumFromFilter(pendingFilters.type?.value)}
          size="small"
        />

        <Select
          className="grid-span-3"
          data-cy="incidents-filter-status"
          getItemLabel={({ description }: IncidentStatus) => description}
          getItemDisabled={({ id }: IncidentStatus) =>
            tab === INCIDENTS_PAGE_TAB_OPEN &&
            !INCIDENT_OPEN_STATUSES.includes(id)
          }
          items={data.incidentStatuses}
          label={translate('incidents.filters.status')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(status: IncidentStatus[]) =>
            setPendingFilters({
              ...pendingFilters,
              status: getFilterItem(
                translate('incidents.filters.status'),
                status,
              ),
            })
          }
          value={getBaseEnumFromFilter(pendingFilters.status?.value)}
          size="small"
        />

        <Select
          className="grid-span-3"
          data-cy="incidents-filter-assignee"
          filterOptions={FilterEmployeeOptions}
          grouped={(item: SelectableInfo<EmployeeGroup | Employee>) => {
            if (item.info instanceof EmployeeGroup) {
              return translate('global.groups');
            }
            if (item.info instanceof Employee) {
              return translate('global.employees');
            }
            return '';
          }}
          items={[...data.assigneeGroups, ...data.assignees]}
          isOptionEqualToValue={(
            a: SelectableInfo<EmployeeGroup | Employee>,
            b: SelectableInfo<EmployeeGroup | Employee>,
          ) => {
            if (a.info instanceof Employee && b.info instanceof Employee)
              return a.value === b.value;
            if (
              a.info instanceof EmployeeGroup &&
              b.info instanceof EmployeeGroup
            )
              return a.value === b.value;
            return false;
          }}
          label={translate('incidents.filters.assignee')}
          limitTags={1}
          loading={loadingFilters}
          multiple
          onChange={(assignees: Selectable[]) =>
            setPendingFilters({
              ...pendingFilters,
              assigneeId: getFilterItem(
                translate('incidents.filters.assignee'),
                assignees,
              ),
            })
          }
          value={pendingFilters.assigneeId?.value}
          size="small"
        />

        <div className="grid-span-3 flex gap">
          <DateTimePicker
            data-cy="incidents-filter-date-from"
            dateFormat="M/D/YY"
            label={translate('incidents.filters.dateFrom')}
            maxDate={
              (getFilterValue(pendingFilters.to?.value) as Moment)?.toDate() ||
              Date.now()
            }
            onChange={(date?: Date | null) => {
              setPendingFilters({
                ...pendingFilters,
                from: getFilterItem(
                  translate('incidents.filters.dateFrom'),
                  date,
                ),
              });
            }}
            size="small"
            value={(getFilterValue(
              pendingFilters.from?.value,
            ) as Moment)?.toDate()}
          />
          <DateTimePicker
            data-cy="incidents-filter-date-from"
            dateFormat="M/D/YY"
            label={translate('incidents.filters.dateTo')}
            minDate={(getFilterValue(
              pendingFilters.from?.value,
            ) as Moment)?.toDate()}
            maxDate={new Date()}
            onChange={(date?: Date | null) => {
              setPendingFilters({
                ...pendingFilters,
                to: getFilterItem(translate('incidents.filters.dateTo'), date),
              });
            }}
            size="small"
            value={(getFilterValue(
              pendingFilters.to?.value,
            ) as Moment)?.toDate()}
          />
        </div>
      </FilterGroup>
    </Panel.FilterModal>
  );
};
